import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notifyNetworkError } from '#client';
import { translationReactNetworkNamespace } from '#i18n';

export type ErrorOption<T extends Error> = ErrorMessage<T> | false;

type ErrorMessage<T extends Error> = ((error: T) => string | undefined) | string;

type NotifyTranslatedNetworkErrorParameters<T extends Error> = {
  defaultTranslationKey: string;
  error: T;
  errorOption?: ErrorOption<T>;
};

export const useNotifyTranslatedNetworkError = () => {
  const { i18n, t: reactNetworkTranslate } = useTranslation([translationReactNetworkNamespace]);
  const { t } = useTranslation();

  const getTranslate = useCallback(
    (translationKey: string) => {
      if (i18n.exists(translationKey, { ns: translationReactNetworkNamespace })) {
        return reactNetworkTranslate(translationKey);
      }

      return t(translationKey);
    },
    [i18n, reactNetworkTranslate, t],
  );

  return useCallback(
    <T extends Error>({ defaultTranslationKey, error, errorOption }: NotifyTranslatedNetworkErrorParameters<T>) => {
      if (errorOption === false) {
        return;
      }

      const translationKey = typeof errorOption === 'function' ? errorOption(error) : errorOption;
      notifyNetworkError(getTranslate(translationKey ?? defaultTranslationKey));
    },
    [getTranslate],
  );
};
